<template>
    <LoadingComponent :props="loading" />

    <div class="col-12" v-if="aiImport && aiImport.id">
        <div class="db-card mb-4 !h-auto">
            <div class="db-card-header">
                <h3 class="db-card-title">{{ $t('menu.ai_imports') }} #{{ aiImport.code }}</h3>
                <div class="db-card-filter">
                    <router-link :to="{ name: 'admin.products.aiImport' }" class="db-btn h-9 py-2 bg-gray-200 hover:opacity-80">
                        <i class="lab lab-line-long-arrow-left"></i>
                        <span>{{ $t('button.imports') }}</span>
                    </router-link>
                    <button
                        type="button"
                        class="db-btn h-9 py-2 px-3 bg-gray-200 hover:opacity-80"
                        @click="refreshView"
                        :disabled="isRefreshingView || isRefreshingStatus || isAnyConfigUpdating"
                        :class="{ 'disabled:opacity-50': isRefreshingView }"
                    >
                        <template v-if="isRefreshingView">
                            <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                            </svg>
                        </template>
                        <template v-else>
                            <i class="fa-solid fa-arrows-rotate"></i>
                        </template>
                    </button>
                    <button
                        type="button"
                        class="db-btn h-9 py-2 px-3 text-white bg-primary hover:opacity-80"
                        @click="confirmAllItems"
                        :disabled="isConfirmingAll || isRefreshingView || isRefreshingStatus || isAnyConfigUpdating"
                        :class="{ 'disabled:opacity-50 disabled:bg-gray': isConfirmingAll || isRefreshingView || isRefreshingStatus || isAnyConfigUpdating }"
                        v-if="permissionChecker('products_edit') && [15, 20, 25].includes(aiImport?.status) && hasConfirmableItems"
                    >
                        <template v-if="isConfirmingAll">
                            <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                            </svg>
                        </template>
                        <template v-else>
                            <i class="fa-regular fa-thumbs-up"></i>
                        </template>
                        <span class="db-tooltip">{{ $t('button.confirm_all') }}</span>
                    </button>
                    <button
                        type="button"
                        class="db-btn h-9 py-2 text-orange-500 border bg-orange-100 border-warning hover:opacity-80"
                        @click="retryImport"
                        :disabled="!canStartFlow || isStarting || isAnyConfigUpdating"
                        :class="{ 'disabled:opacity-50 disabled:bg-gray': isAnyConfigUpdating }"
                        v-if="permissionChecker('products_edit') && canRetryImport"
                    >
                        <template v-if="isStarting">
                            <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                            </svg>
                        </template>
                        <i v-else class="lab lab-line-reset"></i>
                        <span>{{ $t('button.retry') }}</span>
                    </button>
                    <button
                        type="button"
                        class="db-btn h-9 py-2 text-white bg-primary hover:opacity-80"
                        @click="startImport"
                        :disabled="!canStartFlow || isStarting || isAnyConfigUpdating"
                        :class="{ 'disabled:opacity-50 disabled:bg-gray': isAnyConfigUpdating }"
                        v-if="permissionChecker('products_edit') && isPendingImport"
                    >
                        <template v-if="isStarting">
                            <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                            </svg>
                        </template>
                        <i v-else class="lab lab-fill-save"></i>
                        <span>{{ $t('button.confirm_and_start_import') }}</span>
                    </button>
                    <button
                        type="button"
                        class="db-btn h-9 py-2 px-3 text-danger border bg-red-100 border-danger hover:opacity-80"
                        @click="openPurgeModal"
                        :disabled="isPurgingImport || isRefreshingView || isRefreshingStatus || isAnyConfigUpdating"
                        :class="{ 'disabled:opacity-50 disabled:bg-gray': isPurgingImport || isAnyConfigUpdating }"
                        v-if="permissionChecker('products_edit') && canPurgeImport"
                    >
                        <template v-if="isPurgingImport">
                            <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                            </svg>
                        </template>
                        <i v-else class="lab lab-line-trash"></i>
                        <span>{{ $t('button.delete') }}</span>
                    </button>
                </div>
            </div>
            <div class="db-card-body">
                <div class="flex justify-start items-center gap-4 pt-2" :class="isPendingImport ? 'pb-4' : 'pb-2'">
                    <div class="w-fit !py-1.5 border-0 sm:border-r lg:border-r pr-4 flex items-center">
                        <div class="db-list-item gap-2 p-0">
                            <span class="db-list-item-title">{{ $t('label.import_status') }}:</span>
                            <span class="db-list-item-text">{{ statusLabel(aiImport.status) }}</span>
                        </div>
                    </div>
                    <div class="w-fit !py-1.5 border-0 sm:border-r lg:border-r pr-4 flex items-center">
                        <div class="db-list-item gap-2 p-0">
                            <span class="db-list-item-title">{{ $t('label.total_items') }}:</span>
                            <span class="db-list-item-text">{{ aiImport.total_items }}</span>
                        </div>
                    </div>
                    <div class="w-fit !py-1.5 border-0 sm:border-r lg:border-r pr-4 flex items-center">
                        <div class="db-list-item gap-2 p-0">
                            <span class="db-list-item-title">{{ $t('label.incompletes') }}:</span>
                            <span class="db-list-item-text">{{ incompleteItemsCount }}</span>
                        </div>
                    </div>
                    <div class="w-fit !py-1.5 border-0 sm:border-r lg:border-r pr-4 flex items-center">
                        <div class="db-list-item gap-2 p-0">
                            <span class="db-list-item-title">{{ $t('label.creation') }}:</span>
                            <span class="db-list-item-text">{{ simpleDateTime(aiImport.created_at) }}</span>
                        </div>
                    </div>
                    <div class="w-fit !py-1.5 flex items-center">
                        <div class="db-list-item gap-2 p-0">
                            <span class="db-list-item-title">{{ $t('label.importation') }}:</span>
                            <span class="db-list-item-text">{{ simpleDateTime(aiImport.started_at) }}</span>
                        </div>
                    </div>
                </div>

                <div class="form-row mt-3 pt-3 border-t" v-if="isPendingImport">

                    <div class="form-col-12" v-show="isConfigFormExpanded">
                        <div class="form-row">

                            <div class="form-col-12 sm:form-col-6 lg:form-col-3">
                                <label class="db-field-title required">{{ $t('label.default_provider') }}</label>
                                <vue-select
                                    :key="providerSelectKey"
                                    class="db-field-control f-b-custom-select"
                                    v-model="startForm.provider"
                                    :options="safeProviderOptions"
                                    label-by="name"
                                    value-by="id"
                                    :closeOnSelect="true"
                                    :searchable="true"
                                    :clearOnClose="true"
                                />
                            </div>

                            <div class="form-col-12 sm:form-col-6 lg:form-col-3">
                                <label class="db-field-title required">{{ $t('label.default_model') }}</label>
                                <vue-select
                                    :key="modelSelectKey"
                                    class="db-field-control f-b-custom-select"
                                    v-model="startForm.model"
                                    :options="safeActiveModelOptions"
                                    label-by="name"
                                    value-by="id"
                                    :closeOnSelect="true"
                                    :searchable="true"
                                    :clearOnClose="true"
                                />
                            </div>

                            <div class="form-col-12 sm:form-col-6 lg:form-col-3">
                                <label class="db-field-title">{{ $t('label.default_tax_ids') }}</label>
                                <vue-select
                                    :key="taxSelectKey"
                                    class="db-field-control f-b-custom-select"
                                    v-model="startForm.tax_ids"
                                    :options="taxOptions"
                                    label-by="name"
                                    value-by="id"
                                    :multiple="true"
                                    :closeOnSelect="false"
                                    :searchable="true"
                                    :clearOnClose="true"
                                />
                            </div>

                            <div class="form-col-12 sm:form-col-6 lg:form-col-3">
                                <label class="db-field-title">{{ $t('label.default_unit_id') }}</label>
                                <vue-select
                                    :key="unitSelectKey"
                                    class="db-field-control f-b-custom-select"
                                    v-model="startForm.unit_id"
                                    :options="unitOptions"
                                    label-by="name"
                                    value-by="id"
                                    :closeOnSelect="true"
                                    :searchable="true"
                                    :clearOnClose="true"
                                />
                            </div>

                            <div class="form-col-12 sm:form-col-6 lg:form-col-3">
                                <label class="db-field-title">{{ $t('label.barcode') }}</label>
                                <vue-select
                                    :key="barcodeSelectKey"
                                    class="db-field-control f-b-custom-select"
                                    v-model="startForm.barcode_id"
                                    :options="barcodeOptions"
                                    label-by="name"
                                    value-by="id"
                                    :closeOnSelect="true"
                                    :searchable="true"
                                    :clearOnClose="true"
                                />
                            </div>

                            <!-- <div class="form-col-12 sm:form-col-6 lg:form-col-3">
                                <label class="db-field-title required">{{ $t('label.default_wholesale') }}</label>
                                <vue-select
                                    :key="wholesaleSelectKey"
                                    class="db-field-control f-b-custom-select"
                                    v-model="startForm.wholesale"
                                    :options="askOptions"
                                    label-by="name"
                                    value-by="id"
                                    :closeOnSelect="true"
                                    :searchable="true"
                                    :clearOnClose="true"
                                />
                            </div> -->

                            <!-- <div class="form-col-12 sm:form-col-6 lg:form-col-3">
                                <label class="db-field-title">{{ $t('label.default_wholesale_min') }}</label>
                                <input
                                    v-model.number="startForm.wholesale_min"
                                    type="number"
                                    min="1"
                                    class="db-field-control"
                                />
                            </div> -->

                            <div class="form-col-12 sm:form-col-6 lg:form-col-3">
                                <label class="db-field-title required">{{ $t('label.max_items_per_chunk') }}</label>
                                <input
                                    v-model.number="startForm.max_items_per_chunk"
                                    type="number"
                                    min="5"
                                    max="20"
                                    class="db-field-control"
                                />
                            </div>

                            <!-- <div class="form-col-12 sm:form-col-6">
                                <label class="db-field-title">{{ $t('label.default_custom_fields_json') }}</label>
                                <textarea
                                    v-model="customFieldsJson"
                                    class="db-field-control"
                                    rows="5"
                                ></textarea>
                            </div> -->

                        </div>
                    </div>

                    <div class="form-col-12" :class="{ 'border-t pt-4 mt-3': isConfigFormExpanded }">
                        <div class="flex items-center gap-2">
                            <button v-if="isConfigFormExpanded"
                                type="button"
                                class="db-btn h-9 py-2 px-3 text-white bg-primary hover:opacity-80"
                                :class="{ 'disabled:opacity-50': !isConfigFormExpanded || isRefreshingStatus || isRefreshingView || isAnyConfigUpdating }"
                                :disabled="isRefreshingStatus || isRefreshingView || isAnyConfigUpdating || !isConfigFormExpanded"
                                @click="refreshStatusOnly"
                            >
                                <template v-if="isRefreshingStatus">
                                    <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                        <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                    </svg>
                                </template>
                                <template v-else>
                                    <i class="fa-solid fa-arrows-rotate"></i>
                                </template>
                                <span class="db-tooltip">{{ $t('button.update_snapshot_config') }}</span>
                            </button>
                            <button
                                type="button"
                                class="db-btn h-9 py-2 text-white bg-primary hover:opacity-80"
                                :class="{ 'disabled:opacity-50 disabled:bg-gray': !canStart || isUpdatingRetryConfig || isUpdatingPendingSnapshot || !isConfigFormExpanded }"
                                :disabled="!canStart || isStarting || isUpdatingPendingSnapshot || !isConfigFormExpanded"
                                @click="updateSnapshot"
                            >
                                <template v-if="isUpdatingPendingSnapshot">
                                    <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                        <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                    </svg>
                                    <span>{{ $t('button.updating') }}</span>
                                </template>
                                <template v-else>
                                <i class="fa-solid fa-floppy-disk"></i>
                                    <span>{{ $t('button.update_snapshot') }}</span>
                                </template>
                            </button>
                            <button
                                type="button"
                                class="db-btn py-2 px-3 bg-gray-200"
                                @click="toggleConfigForm"
                                :aria-expanded="isConfigFormExpanded ? 'true' : 'false'"
                            >
                                <svg
                                    class="config-chevron"
                                    :class="{ 'is-open': isConfigFormExpanded }"
                                    viewBox="0 0 20 20"
                                    width="16"
                                    height="16"
                                    aria-hidden="true"
                                >
                                    <path d="M5 7.5L10 12.5L15 7.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </button>
                        </div>
                    </div>

                </div>

                <div class="form-row mt-3 pt-3 border-t" v-if="canRetryImport">

                    <div class="form-col-12" v-show="isRetryConfigFormExpanded">
                        <div class="form-row">

                            <div class="form-col-12 sm:form-col-6 lg:form-col-3">
                                <label class="db-field-title required">{{ $t('label.default_provider') }}</label>
                                <vue-select
                                    :key="retryProviderSelectKey"
                                    class="db-field-control f-b-custom-select"
                                    v-model="retryForm.provider"
                                    :options="safeProviderOptions"
                                    label-by="name"
                                    value-by="id"
                                    :closeOnSelect="true"
                                    :searchable="true"
                                    :clearOnClose="true"
                                />
                            </div>

                            <div class="form-col-12 sm:form-col-6 lg:form-col-3">
                                <label class="db-field-title required">{{ $t('label.default_model') }}</label>
                                <vue-select
                                    :key="retryModelSelectKey"
                                    class="db-field-control f-b-custom-select"
                                    v-model="retryForm.model"
                                    :options="safeRetryModelOptions"
                                    label-by="name"
                                    value-by="id"
                                    :closeOnSelect="true"
                                    :searchable="true"
                                    :clearOnClose="true"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="form-col-12" :class="{ 'border-t pt-4 mt-3': isRetryConfigFormExpanded }">
                        <div class="flex items-center gap-2">
                            <button
                                type="button"
                                class="db-btn h-9 py-2 text-white bg-primary"
                                :class="{ 'disabled:opacity-50 disabled:bg-gray': !canRetryImport || isUpdatingRetryConfig || isStarting || !isRetryConfigFormExpanded }"
                                :disabled="!canRetryImport || isUpdatingRetryConfig || isStarting || !isRetryConfigFormExpanded"
                                @click="updateRetryLlmConfig"
                            >
                                <template v-if="isUpdatingRetryConfig">
                                    <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                        <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                    </svg>
                                    <span>{{ $t('button.updating') }}</span>
                                </template>
                                <template v-else>
                                    <i class="fa-solid fa-floppy-disk"></i>
                                    <span>{{ $t('button.update_provider') }}</span>
                                </template>
                            </button>
                            <button
                                type="button"
                                class="db-btn py-2 px-3 bg-gray-200"
                                @click="toggleRetryConfigForm"
                                :aria-expanded="isRetryConfigFormExpanded ? 'true' : 'false'"
                            >
                                <svg
                                    class="config-chevron"
                                    :class="{ 'is-open': isRetryConfigFormExpanded }"
                                    viewBox="0 0 20 20"
                                    width="16"
                                    height="16"
                                    aria-hidden="true"
                                >
                                    <path d="M5 7.5L10 12.5L15 7.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="db-card !h-auto" v-if="canRenderItemsTable && isPendingImport">
            <div class="db-card-header border-none">
                <h3 class="db-card-title pt-1">{{ $t('label.items_to_import') }}</h3>
            </div>
            <div class="db-table-responsive">
                <table class="db-table stripe">
                    <thead class="db-table-head">
                        <tr class="db-table-head-tr">
                            <th class="db-table-head-th">#</th>
                            <th class="db-table-head-th">{{ $t('label.sku') }}</th>
                            <th class="db-table-head-th">{{ $t('label.name') }}</th>
                            <th class="db-table-head-th">{{ $t('label.brand') }}</th>
                            <th class="db-table-head-th">{{ $t('label.buying_price_alt') }}</th>
                            <th class="db-table-head-th">{{ $t('label.selling_price_alt') }}</th>
                            <th class="db-table-head-th">{{ $t('label.unit') }}</th>
                            <th class="db-table-head-th">{{ $t('label.quantity') }}</th>
                        </tr>
                    </thead>
                    <tbody class="db-table-body" v-if="items.length">
                        <tr class="db-table-body-tr" v-for="item in items" :key="item.id">
                            <td class="db-table-body-td">{{ item.line_number }}</td>
                            <td class="db-table-body-td">{{ item.sku }}</td>
                            <td class="db-table-body-td">{{ item.name }}</td>
                            <td class="db-table-body-td">{{ item.brand?.name || '--' }}</td>
                            <td class="db-table-body-td">{{ formatPrice2(item.buying_price) }}</td>
                            <td class="db-table-body-td">{{ formatPrice2(item.selling_price) }}</td>
                            <td class="db-table-body-td">{{ item.unit?.name || '--' }}</td>
                            <td class="db-table-body-td">{{ item.stock_quantity }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="db-card !h-auto" v-else-if="canRenderItemsTable">
            <div class="db-table-responsive">
                <table class="db-table stripe">
                    <thead class="db-table-head">
                        <tr class="db-table-head-tr no-border-top">
                            <th class="db-table-head-th">#</th>
                            <th class="db-table-head-th">{{ $t('label.name') }}</th>
                            <th class="db-table-head-th">{{ $t('label.category') }}</th>
                            <th class="db-table-head-th">{{ $t('label.tags') }}</th>
                            <th class="db-table-head-th">{{ $t('label.weight') }}</th>
                            <th class="db-table-head-th">{{ $t('label.height') }}</th>
                            <th class="db-table-head-th">{{ $t('label.width') }}</th>
                            <th class="db-table-head-th">{{ $t('label.length') }}</th>
                            <th class="db-table-head-th">{{ $t('label.note') }}</th>
                            <th class="db-table-head-th">{{ $t('label.status') }}</th>
                            <th class="db-table-head-th text-right">{{ $t('label.action') }}</th>
                        </tr>
                    </thead>
                    <tbody class="db-table-body" v-if="items.length">
                        <tr class="db-table-body-tr" v-for="item in items" :key="item.id">
                            <td class="db-table-body-td">{{ item.line_number }}</td>
                            <td class="db-table-body-td">
                                <input
                                    class="db-field-control h-7 px-2"
                                    :value="getFieldValue(item, 'name')"
                                    :disabled="!isEditable"
                                    @blur="onFieldBlur(item, 'name', $event.target.value)"
                                />
                            </td>
                            <td class="db-table-body-td">
                                <select
                                    class="db-field-control h-7 px-2"
                                    :disabled="!isEditable"
                                    :value="getFieldValue(item, 'category')"
                                    @change="onFieldChange(item, 'category', $event.target.value)"
                                >
                                    <option value="">--</option>
                                    <option
                                        v-for="category in categoryOptions"
                                        :key="`cat-${category.id}`"
                                        :value="String(category.id)"
                                    >
                                        {{ category.name }}
                                    </option>
                                </select>
                            </td>
                            <td class="db-table-body-td">
                                <input
                                    class="db-field-control h-7 px-2"
                                    :value="formatTags(getFieldValue(item, 'tags'))"
                                    :disabled="!isEditable"
                                    @blur="onFieldBlur(item, 'tags', $event.target.value)"
                                />
                            </td>
                            <td class="db-table-body-td">
                                <input
                                    class="db-field-control h-7 px-2"
                                    :value="getFieldValue(item, 'weight')"
                                    :disabled="!isEditable"
                                    @blur="onFieldBlur(item, 'weight', $event.target.value)"
                                />
                            </td>
                            <td class="db-table-body-td">
                                <input
                                    class="db-field-control h-7 px-2"
                                    :value="getFieldValue(item, 'height')"
                                    :disabled="!isEditable"
                                    @blur="onFieldBlur(item, 'height', $event.target.value)"
                                />
                            </td>
                            <td class="db-table-body-td">
                                <input
                                    class="db-field-control h-7 px-2"
                                    :value="getFieldValue(item, 'width')"
                                    :disabled="!isEditable"
                                    @blur="onFieldBlur(item, 'width', $event.target.value)"
                                />
                            </td>
                            <td class="db-table-body-td">
                                <input
                                    class="db-field-control h-7 px-2"
                                    :value="getFieldValue(item, 'length')"
                                    :disabled="!isEditable"
                                    @blur="onFieldBlur(item, 'length', $event.target.value)"
                                />
                            </td>
                            <td class="db-table-body-td text-center">
                                <button
                                    v-if="item.error_message && [15, 20, 25].includes(Number(aiImport?.status))"
                                    type="button"
                                    class="db-btn p-1 rounded-xl hover:opacity-80"
                                    :class="item.is_incomplete ? 'bg-red-100 text-red-500' : 'border border-warning bg-orange-100 text-orange-500'"
                                    @click="openNoteModal(item.error_message, processedField(item, 'name'))"
                                >
                                    <span class="text-xs h-4 w-4 pt-0.5 flex items-center justify-center font-bold font-admin">!</span>
                                </button>
                                <span v-else>— —</span>
                            </td>
                            <td class="db-table-body-td">
                                <button v-if="hasPendingChanges(item)" type="button" class="db-btn w-full text-xs font-medium h-7 py-1 px-2 text-white bg-primary hover:opacity-80 disabled:opacity-50" @click="updateRow(item)" :disabled="updatingTableItemId === item.id">
                                    <template v-if="updatingTableItemId === item.id">
                                        <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                                            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                            <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                        </svg>
                                    </template>
                                    <template v-else>
                                        {{ $t('button.save') }}
                                    </template>
                                </button>
                                <span v-else :class="itemBadge(item)">{{ itemState(item) }}</span>
                            </td>
                            <td class="db-table-body-td">
                                <div class="flex items-center justify-end gap-2">
                                    <button
                                        v-if="item.product_id"
                                        type="button"
                                        class="db-btn-outline sm default primary transparent hover:opacity-80 disabled:opacity-50"
                                        :disabled="updatingTableItemId === item.id"
                                        @click="openProductDetailModal(item)"
                                    >
                                        <template v-if="updatingTableItemId === item.id">
                                            <svg class="inline-block animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                            </svg>
                                        </template>
                                        <template v-else>
                                            <i class="lab lab-fill-document text-md"></i>
                                        </template>
                                        <span class="db-tooltip">{{ $t('button.view_fields') }}</span>
                                    </button>
                                    <template v-if="Number(item.status) === 25">
                                        <button
                                            type="button"
                                            class="db-btn-outline sm default primary transparent opacity-50 cursor-not-allowed"
                                            disabled
                                        >
                                            <i class="fa-solid fa-check"></i>
                                            <span class="db-tooltip">{{ $t('label.confirmed') || 'Confirmado' }}</span>
                                        </button>
                                    </template>
                                    <template v-else-if="canConfirmItem(item)">
                                        <button
                                            type="button"
                                            class="db-btn-outline sm default primary transparent hover:opacity-80 disabled:opacity-50"
                                            @click="confirmItem(item)"
                                            :disabled="confirmingItemId === item.id || deletingItemId === item.id"
                                            v-if="permissionChecker('products_edit')"
                                        >
                                            <template v-if="confirmingItemId === item.id">
                                                <svg class="inline-block animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                                </svg>
                                            </template>
                                            <template v-else>
                                                <i class="fa-regular fa-thumbs-up text-md"></i>
                                            </template>
                                            <span class="db-tooltip">{{ $t('button.confirm') }}</span>
                                        </button>
                                    </template>
                                    <template v-else>
                                        <button
                                            type="button"
                                            class="db-btn-outline sm default transparent hover:opacity-80 disabled:opacity-50"
                                            @click="deleteItem(item)"
                                            :disabled="deletingItemId === item.id || confirmingItemId === item.id"
                                            v-if="permissionChecker('products_edit')"
                                        >
                                            <template v-if="deletingItemId === item.id">
                                                <svg class="inline-block animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                                </svg>
                                            </template>
                                            <template v-else>
                                                <i class="lab lab-line-trash text-danger text-md"></i>
                                            </template>
                                            <span class="db-tooltip">{{ $t('button.delete') }}</span>
                                        </button>
                                    </template>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div v-if="productDetailModal.visible" class="fixed inset-0 z-50 flex items-center justify-center bg-light/50 p-4 modal-backdrop" @focusin.capture="guardFocusDuringModalUpdate">
            <div class="product-detail-modal relative w-full max-w-4xl rounded-lg bg-white border shadow-md">
                <div class="relative px-5 pt-4 pb-3 border-b">
                    <h4 class="text-lg font-semibold text-center text-primary">{{ $t('label.product_details') }}</h4>
                    <button type="button" class="absolute right-3 top-3 text-gray-500 hover:text-gray-700" @click="closeProductDetailModal">
                        <i class="lab lab-fill-close"></i>
                    </button>
                </div>

                <div class="px-5 pt-5 pb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" :class="{ 'pointer-events-none': isModalBusy || isModalProcessing }">
                    <div class="modal-product space-y-1" v-for="field in productDetailFields" :key="field.key">
                        <label class="db-field-title">{{ field.label }}</label>

                        <vue-select
                            v-if="field.type === 'select'"
                            class="db-field-control f-b-custom-select h-8"
                            :options="field.options"
                            label-by="name"
                            value-by="id"
                            :multiple="field.key === 'category'"
                            :closeOnSelect="field.key !== 'category'"
                            :searchable="true"
                            :clearOnClose="true"
                            :disabled="!isEditable || isModalBusy"
                            :modelValue="safeModalSelectValue(field.key)"
                            @update:modelValue="onModalSelectChange(field.key, $event)"
                        />

                        <input
                            v-else-if="field.type === 'textarea'"
                            :ref="`modalField_${field.key}`"
                            :class="[modalControlClass, 'h-8 px-2']"
                            :value="modalDisplayValue(field.key)"
                            :readonly="!isEditable || isModalBusy || isModalProcessing || productDetailModal.activeField !== field.key"
                            @click="activateModalField(field.key)"
                            @focus="activateModalField(field.key)"
                            @blur="deactivateModalField(field.key, $event.target.value)"
                        ></input>

                        <input
                            v-else
                            :ref="`modalField_${field.key}`"
                            :class="[modalControlClass, 'h-8 px-2']"
                            :value="modalDisplayValue(field.key)"
                            :readonly="!isEditable || isModalBusy || isModalProcessing || productDetailModal.activeField !== field.key"
                            @click="activateModalField(field.key)"
                            @focus="activateModalField(field.key)"
                            @blur="deactivateModalField(field.key, $event.target.value)"
                        />
                    </div>
                </div>

                <div class="w-full px-5 pb-6" v-if="modalCustomFields.length" :class="{ 'pointer-events-none': isModalBusy || isModalProcessing }">
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div class="modal-product space-y-1" v-for="(row, index) in modalCustomFields" :key="`cf-${index}`">
                            <label class="db-field-title">{{ row.label || row.key }}</label>
                            <input
                                :class="[modalControlClass, 'h-8 px-2']"
                                :value="row.value"
                                :readonly="!isEditable || isModalBusy || isModalProcessing || productDetailModal.activeField !== `custom_fields.${index}.value`"
                                @click="activateModalField(`custom_fields.${index}.value`)"
                                @focus="activateModalField(`custom_fields.${index}.value`)"
                                @blur="onCustomFieldBlur(index, $event.target.value)"
                            />
                        </div>
                    </div>
                </div>

                <div class="px-5 py-4 border-t flex justify-end gap-2">
                    <button type="button" class="db-btn h-9 py-2 px-4 bg-gray-200" :disabled="isModalBusy || isModalProcessing" @click="closeProductDetailModal">
                        <i class="lab lab-fill-close"></i>
                        {{ $t('button.close') }}
                    </button>
                    <button
                        type="button"
                        class="db-btn h-9 py-2 px-4 text-white bg-primary disabled:opacity-50 disabled:bg-gray"
                        :disabled="isModalBusy || isModalProcessing || !productDetailModal.item || !isEditable"
                        @click="updateModalItem"
                    >
                        <template v-if="productDetailModal.isUpdating">
                            <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                            </svg>
                            <span>{{ $t('button.updating') }}</span>
                        </template>
                        <template v-else>
                            <i class="fa-solid fa-floppy-disk"></i>
                            <span>{{ $t('label.update') }}</span>
                        </template>
                    </button>
                    <button
                        type="button"
                        class="db-btn h-9 py-2 px-4 text-white bg-primary disabled:opacity-50 disabled:bg-gray"
                        :disabled="isModalBusy || isModalProcessing || !productDetailModal.item || !canConfirmItem(productDetailModal.item)"
                        @click="confirmItem(productDetailModal.item, { fromModal: true })"
                    >
                        <template v-if="productDetailModal.isConfirming">
                            <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                            </svg>
                            <span>{{ $t('button.processing') }}</span>
                        </template>
                        <template v-else>
                            <i class="fa-regular fa-thumbs-up"></i>
                            <span>{{ $t('button.confirm') }}</span>
                        </template>
                    </button>
                </div>
            </div>
        </div>

        <div v-if="noteModal.visible" class="fixed inset-0 z-50 flex items-center justify-center bg-light/50 p-4 modal-backdrop" @click.self="closeNoteModal">
            <div class="w-full max-w-2xl rounded-lg bg-white shadow-md border overflow-hidden">
                <div class="relative px-4 pt-4 pb-2 text-center border-b">
                    <h4 class="text-lg font-semibold text-primary">{{ $t('label.issues_found') }}</h4>
                    <button type="button" class="absolute right-3 top-3 text-gray-500 hover:text-gray-700" @click="closeNoteModal">
                        <i class="lab lab-fill-close"></i>
                    </button>
                </div>
                <div class="px-5 py-4 text-left text-sm whitespace-pre-line overflow-y-auto" style="max-height: 50vh;">
                    <p class="mb-2 text-gray-700">{{ $t('label.product') }}: <strong>{{ noteModal.name }}</strong></p>
                    <div class="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg font-mono text-xs overflow-x-auto select-all">
                        {{ noteModal.message }}
                    </div>
                </div>
            </div>
        </div>

        <div v-if="purgeModal.visible" class="fixed inset-0 z-50 flex items-center justify-center bg-light/50 p-4 modal-backdrop" @click.self="closePurgeModal">
            <div class="w-full max-w-xl rounded-lg bg-white shadow-md border">
                <div class="relative px-4 pt-4 pb-2 border-b">
                    <h4 class="text-lg font-medium font-client text-danger">{{ $t('label.delete_failed_import') }}</h4>
                    <button type="button" class="absolute right-3 top-4 text-gray-500 hover:text-gray-700" @click="closePurgeModal">
                        <i class="lab lab-fill-close"></i>
                    </button>
                </div>

                <div class="px-5 py-4 text-sm space-y-2">
                    <p class="font-client text-sm">
                        {{ $t('message.ai_import_purge_modal_intro_prefix') }}
                        <strong>#{{ aiImport.code }}</strong>
                        {{ $t('message.ai_import_purge_modal_intro_suffix') }}
                    </p>
                    <hr class="w-full border-gray-100 my-4" />
                    <p class="font-admin text-sm">
                        {{ $t('message.ai_import_purge_modal_items_detected_prefix') }} <strong>{{ Number(aiImport.total_items || 0) }}</strong>
                    </p>
                    <hr class="w-full border-gray-100 my-4" />
                    <p class="font-client text-xs mb-2">
                        {{ $t('message.ai_import_purge_modal_safe_delete') }}
                    </p>
                    <p class="text-danger font-medium font-client text-sm">
                        {{ $t('message.ai_import_purge_modal_irreversible') }}
                    </p>
                </div>

                <div class="px-4 py-3 border-t flex justify-end gap-2">
                    <button type="button" class="db-btn py-2 px-4 bg-gray-200" @click="closePurgeModal" :disabled="isPurgingImport">
                        <i class="lab lab-fill-close"></i>
                        <span>{{ $t('button.cancel') }}</span>
                    </button>
                    <button
                        type="button"
                        class="db-btn py-2 px-4 text-white bg-danger disabled:opacity-50 disabled:bg-gray"
                        :disabled="isPurgingImport"
                        @click="confirmPurgeModal"
                    >
                        <template v-if="isPurgingImport">
                            <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                            </svg>
                            <span>{{ $t('button.processing') }}</span>
                        </template>
                        <template v-else>
                            <i class="lab lab-line-trash"></i>
                            <span>{{ $t('button.delete') }}</span>
                        </template>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import LoadingComponent from '../../components/LoadingComponent';
import alertService from '../../../../services/alertService';
import appService from '../../../../services/appService';
import askEnum from '../../../../enums/modules/askEnum';
import VueElementLoading from 'vue-element-loading';
import ENV from "../../../../config/env";

export default {
    name: 'AiImportShowComponent',
    components: { LoadingComponent, VueElementLoading },
    data() {
        return {
            loading: {
                isActive: false,
            },
            isStarting: false,
            isUpdatingPendingSnapshot: false,
            isUpdatingRetryConfig: false,
            isRefreshingStatus: false,
            isRefreshingView: false,
            isPurgingImport: false,
            isConfirmingAll: false,
            importSettingDefaults: {},
            providerRows: [],
            providerOptions: [],
            providerDefault: null,
            providerSelectKey: 0,
            modelSelectKey: 0,
            retryProviderSelectKey: 0,
            retryModelSelectKey: 0,
            taxSelectKey: 0,
            unitSelectKey: 0,
            barcodeSelectKey: 0,
            wholesaleSelectKey: 0,
            customFieldsJson: '{}',
            isConfigFormExpanded: false,
            isRetryConfigFormExpanded: false,
            startForm: {
                max_items_per_chunk: 20,
                provider: null,
                model: null,
                tax_ids: [],
                unit_id: null,
                barcode_id: 1,
                wholesale: askEnum.NO,
                wholesale_min: null,
                custom_fields: {},
            },
            retryForm: {
                provider: null,
                model: null,
            },
            askOptions: [
                { id: askEnum.YES, name: this.$t('label.yes') },
                { id: askEnum.NO, name: this.$t('label.no') },
            ],
            productCategories: [],
            noteModal: {
                visible: false,
                name: '',
                message: '',
            },
            purgeModal: {
                visible: false,
            },
            confirmingItemId: null,
            deletingItemId: null,
            updatingTableItemId: null,
            pendingChanges: {},
            productDetailModal: {
                visible: false,
                item: null,
                form: {},
                activeField: null,
                isLoading: false,
                isUpdating: false,
                isConfirming: false,
                lockField: null,
            },
        };
    },
    computed: {
        spinnerColor: function () {
            return ENV.LOADER_COLOR;
        },
        aiImport() {
            return this.$store.getters['aiImport/show'];
        },
        items() {
            return this.aiImport?.items || [];
        },
        incompleteItemsCount() {
            return this.items.filter((item) => Number(item?.status) === 20 || !!item?.is_incomplete).length;
        },
        taxes() {
            return this.$store.getters['tax/lists'] || [];
        },
        units() {
            return this.$store.getters['unit/lists'] || [];
        },
        brands() {
            return this.$store.getters['productBrand/lists'] || [];
        },
        barcodes() {
            return this.$store.getters['barcode/lists'] || [];
        },
        categoryOptions() {
            return this.productCategories;
        },
        taxOptions() {
            return this.taxes.map((item) => ({
                id: Number(item.id),
                name: item.name || item.code || `#${item.id}`,
            }));
        },
        unitOptions() {
            return this.units.map((item) => ({
                id: Number(item.id),
                name: item.name || item.code || `#${item.id}`,
            }));
        },
        brandOptions() {
            return this.brands.map((item) => ({
                id: Number(item.id),
                name: item.name || `#${item.id}`,
            }));
        },
        barcodeOptions() {
            return this.barcodes.map((item) => ({
                id: Number(item.id),
                name: item.name || `#${item.id}`,
            }));
        },
        productDetailFields() {
            return [
                { key: 'name', label: this.$t('label.name') },
                { key: 'sku', label: this.$t('label.sku') },
                { key: 'category', label: this.$t('label.category'), type: 'select', options: this.categoryOptions },
                { key: 'product_brand_id', label: this.$t('label.brand'), type: 'select', options: this.brandOptions },
                { key: 'unit_id', label: this.$t('label.unit'), type: 'select', options: this.unitOptions },
                { key: 'tags', label: this.$t('label.tags') },
                { key: 'buying_price', label: this.$t('label.buying_price_alt') },
                { key: 'selling_price', label: this.$t('label.selling_price_alt') },
                { key: 'wholesale_price', label: this.$t('label.wholesale_price_alt') },
                { key: 'stock_quantity', label: this.$t('label.quantity') },
                { key: 'weight', label: this.$t('label.weight') },
                { key: 'height', label: this.$t('label.height') },
                { key: 'width', label: this.$t('label.width') },
                { key: 'length', label: this.$t('label.length') },
                { key: 'short_description', label: this.$t('label.short_description'), type: 'textarea' },
                { key: 'description', label: this.$t('label.description'), type: 'textarea' },
            ];
        },
        modalCustomFields() {
            return this.productDetailModal.form?.custom_fields || [];
        },
        modalControlClass() {
            return 'db-field-control !ring-0 ring-0 focus:ring-0 focus-visible:ring-0 !outline-none outline-none focus:outline-none focus-visible:outline-none focus:border-secondary';
        },
        modalCategoryValues() {
            return this.normalizeCategorySelection(this.productDetailModal.form?.category || []);
        },
        isModalBusy() {
            return !!this.productDetailModal?.isLoading;
        },
        isModalProcessing() {
            return !!(this.productDetailModal?.isUpdating || this.productDetailModal?.isConfirming);
        },
        safeProviderOptions() {
            return this.providerOptions.filter((item) => item && item.id !== undefined && item.id !== null && item.id !== '');
        },
        activeModelOptions() {
            const provider = this.startForm.provider;
            const selectedModel = this.normalizeScalarSelectValue(this.startForm.model);
            return this.providerRows
                .filter((item) => item.provider === provider && (item.is_enabled || item.model === selectedModel))
                .filter((item) => !!item.model)
                .map((item) => ({
                    id: item.model,
                    name: item.model,
                }));
        },
        safeActiveModelOptions() {
            return this.activeModelOptions.filter((item) => item && item.id !== undefined && item.id !== null && item.id !== '');
        },
        retryModelOptions() {
            const provider = this.retryForm.provider;
            const selectedModel = this.normalizeScalarSelectValue(this.retryForm.model);
            return this.providerRows
                .filter((item) => item.provider === provider && (item.is_enabled || item.model === selectedModel))
                .filter((item) => !!item.model)
                .map((item) => ({
                    id: item.model,
                    name: item.model,
                }));
        },
        safeRetryModelOptions() {
            return this.retryModelOptions.filter((item) => item && item.id !== undefined && item.id !== null && item.id !== '');
        },
        isPendingImport() {
            return this.aiImport?.status === 5;
        },
        isEditable() {
            return [5, 15, 20].includes(this.aiImport?.status);
        },
        canStart() {
            return this.aiImport?.status === 5;
        },
        canRetryImport() {
            const hasErrors = [20, 30].includes(this.aiImport?.status);
            const hasItemsWithoutProduct = this.items.some(item => !item.product_id);
            return hasErrors && hasItemsWithoutProduct;
        },
        canStartFlow() {
            return [5, 20, 30].includes(this.aiImport?.status);
        },
        canConfigureImport() {
            return [5, 20, 30].includes(this.aiImport?.status);
        },
        canPurgeImport() {
            return !!this.aiImport?.can_purge;
        },
        isAnyConfigUpdating() {
            return this.isUpdatingPendingSnapshot || this.isUpdatingRetryConfig;
        },
        hasConfirmableItems() {
            return this.items.some((item) => this.canConfirmItem(item));
        },
        canRenderItemsTable() {
            return !this.loading.isActive && this.aiImport?.status !== null && this.aiImport?.status !== undefined;
        },
    },
    watch: {
        'startForm.provider'() {
            this.modelSelectKey += 1;
            this.ensureModelSelection();
        },
        'retryForm.provider'() {
            this.retryModelSelectKey += 1;
            this.ensureRetryModelSelection();
        },
    },
    async mounted() {
        await Promise.all([
            this.fetchDependencies(),
            this.fetch(),
        ]);
        this.syncStartFormWithDefaults();

        window.addEventListener('ai-import:status-refresh', this.onAiImportStatusRefresh);
        window.addEventListener('storage', this.onAiImportStatusRefreshStorage);
    },
    beforeUnmount() {
        window.removeEventListener('ai-import:status-refresh', this.onAiImportStatusRefresh);
        window.removeEventListener('storage', this.onAiImportStatusRefreshStorage);
    },
    methods: {
        normalizeScalarSelectValue(value) {
            if (value && typeof value === 'object') {
                return value.id ?? value.value ?? null;
            }

            return value ?? null;
        },
        async fetchDependencies() {
            const [providerRes, importSettingRes] = await Promise.all([
                this.$store.dispatch('aiProviderSetting/providerSettings'),
                this.$store.dispatch('aiImportSetting/importSettingRefresh'),
                this.$store.dispatch('tax/lists', {
                    paginate: 0,
                    order_column: 'id',
                    order_type: 'asc',
                }),
                this.$store.dispatch('unit/lists', {
                    paginate: 0,
                    order_column: 'id',
                    order_type: 'asc',
                }),
                this.$store.dispatch('productBrand/lists', {
                    paginate: 0,
                    order_column: 'name',
                    order_type: 'asc',
                    vuex: true,
                }),
                this.$store.dispatch('barcode/lists', {
                    paginate: 0,
                    order_column: 'id',
                    order_type: 'asc',
                }),
                this.$store.dispatch('productCategory/depthTrees', {
                    paginate: 0,
                    status: 5,
                    order_column: 'name',
                    order_type: 'asc',
                }),
            ]);

            this.importSettingDefaults = importSettingRes?.data?.data || {};
            const providers = providerRes?.data?.data || [];

            this.providerDefault = (providers.find((provider) => !!provider.is_default && !!provider.is_enabled)?.provider)
                || (providers.find((provider) => !!provider.is_enabled)?.provider)
                || null;

            this.providerOptions = providers
                .filter((provider) => !!provider.is_enabled)
                .filter((provider) => !!provider.provider)
                .map((provider) => ({
                    id: provider.provider,
                    name: this.providerLabel(provider.provider),
                }));

            this.providerRows = providers
                .flatMap((provider) => {
                    return (provider.models || [])
                        .filter((modelItem) => !!modelItem.model)
                        .map((modelItem) => ({
                            provider: provider.provider,
                            model: modelItem.model,
                            is_enabled: !!modelItem.is_enabled,
                            is_default: !!modelItem.is_default,
                        }));
                });

            this.productCategories = this.flattenCategoryTree(this.$store.getters['productCategory/depthTrees'] || []);

            this.providerSelectKey += 1;
            this.modelSelectKey += 1;
        },
        permissionChecker(permissionName) {
            return appService.permissionChecker(permissionName);
        },
        async fetch() {
            this.loading.isActive = true;
            await this.$store.dispatch('aiImport/show', this.$route.params.id).finally(() => {
                this.loading.isActive = false;
            });

            this.syncStartFormWithDefaults();
        },
        async refreshView() {
            if (this.isRefreshingView || this.isRefreshingStatus) {
                return;
            }

            this.isRefreshingView = true;
            try {
                await Promise.all([
                    this.fetchDependencies(),
                    this.$store.dispatch('aiImport/show', this.$route.params.id),
                ]);
                this.syncStartFormWithDefaults();
                alertService.info(this.$t('label.data_refreshed'));
            } finally {
                this.isRefreshingView = false;
            }
        },
        async refreshStatusOnly() {
            if (this.isRefreshingStatus || this.isRefreshingView) {
                return;
            }

            this.isRefreshingStatus = true;
            try {
                await this.$store.dispatch('aiImport/show', this.$route.params.id);
                this.syncStartFormWithDefaults();
            } finally {
                this.isRefreshingStatus = false;
            }
        },
        async onAiImportStatusRefresh(event) {
            const detail = event?.detail || {};
            const command = String(detail.command || '');
            const status = String(detail.status || '');
            const autoRefreshCommands = ['queue_process_pending', 'queue_failed_retry_all', 'queue_failed_clear'];

            if (!autoRefreshCommands.includes(command) || status !== 'ok') {
                return;
            }

            await this.refreshStatusOnly();
        },
        async onAiImportStatusRefreshStorage(event) {
            if (event?.key !== 'ai-import-status-refresh' || !event.newValue) {
                return;
            }

            try {
                const payload = JSON.parse(event.newValue);
                await this.onAiImportStatusRefresh({ detail: payload });
            } catch (e) {
                // Ignore malformed storage events.
            }
        },
        syncStartFormWithDefaults() {
            if (!this.aiImport || !this.aiImport.id) {
                return;
            }

            const snapshot = this.aiImport.config_snapshot || {};
            const defaults = this.importSettingDefaults || {};

            const chunk = Number(snapshot.max_items_per_chunk ?? defaults.max_items_per_chunk ?? 20);
            this.startForm.max_items_per_chunk = Math.max(5, Math.min(20, Number.isNaN(chunk) ? 20 : chunk));

            const providerFallback = this.safeProviderOptions.find((item) => item.id === this.providerDefault)
                || this.safeProviderOptions[0]
                || null;

            this.startForm.provider = snapshot.provider
                || defaults.default_provider
                || providerFallback?.id
                || null;
            this.startForm.provider = this.normalizeScalarSelectValue(this.startForm.provider);

            if (!this.safeProviderOptions.some((item) => item.id === this.startForm.provider)) {
                this.startForm.provider = providerFallback?.id || null;
            }

            this.startForm.model = this.normalizeScalarSelectValue(snapshot.model || defaults.default_model || null);
            this.ensureModelSelection();

            this.retryForm.provider = this.startForm.provider;
            this.retryForm.model = this.startForm.model;
            this.ensureRetryModelSelection();

            this.startForm.tax_ids = this.parseTaxIds(snapshot.tax_ids ?? defaults.default_tax_ids);

            const snapshotUnit = snapshot.unit_id ? Number(snapshot.unit_id) : null;
            const defaultUnit = defaults.default_unit_id ? Number(defaults.default_unit_id) : null;
            this.startForm.unit_id = snapshotUnit || defaultUnit || (this.unitOptions[0]?.id || null);

            const snapshotBarcode = snapshot.barcode_id ? Number(snapshot.barcode_id) : null;
            const defaultBarcode = defaults.default_barcode_id ? Number(defaults.default_barcode_id) : null;
            this.startForm.barcode_id = snapshotBarcode || defaultBarcode || (this.barcodeOptions[0]?.id || 1);
            if (!this.barcodeOptions.some((item) => item.id === this.startForm.barcode_id) && this.barcodeOptions.length) {
                this.startForm.barcode_id = this.barcodeOptions[0].id;
            }

            this.startForm.wholesale = Number(snapshot.wholesale ?? defaults.default_wholesale ?? askEnum.NO);
            if (![askEnum.YES, askEnum.NO].includes(this.startForm.wholesale)) {
                this.startForm.wholesale = askEnum.NO;
            }

            this.startForm.wholesale_min = snapshot.wholesale_min ?? defaults.default_wholesale_min ?? null;

            const defaultCustomFields = this.normalizeCustomFieldsValue(defaults.default_custom_fields);
            const snapshotCustomFields = this.normalizeCustomFieldsValue(snapshot.custom_fields);

            const customFields = Array.isArray(snapshotCustomFields)
                ? (snapshotCustomFields.length ? snapshotCustomFields : defaultCustomFields)
                : (Object.keys(snapshotCustomFields).length ? snapshotCustomFields : defaultCustomFields);

            this.startForm.custom_fields = customFields;
            this.customFieldsJson = JSON.stringify(customFields, null, 2);
            this.providerSelectKey += 1;
            this.modelSelectKey += 1;
            this.retryProviderSelectKey += 1;
            this.retryModelSelectKey += 1;
            this.taxSelectKey += 1;
            this.unitSelectKey += 1;
            this.barcodeSelectKey += 1;
            this.wholesaleSelectKey += 1;
        },
        ensureModelSelection() {
            const hasModel = this.safeActiveModelOptions.some((item) => item.id === this.startForm.model);
            if (!hasModel) {
                const providerRows = this.providerRows.filter((item) => item.provider === this.startForm.provider && item.is_enabled);
                const defaultModel = providerRows.find((item) => item.is_default && item.model);
                const firstModel = providerRows.find((item) => item.model);
                this.startForm.model = defaultModel?.model || firstModel?.model || null;
            }
        },
        ensureRetryModelSelection() {
            const hasModel = this.safeRetryModelOptions.some((item) => item.id === this.retryForm.model);
            if (!hasModel) {
                const providerRows = this.providerRows.filter((item) => item.provider === this.retryForm.provider && item.is_enabled);
                const defaultModel = providerRows.find((item) => item.is_default && item.model);
                const firstModel = providerRows.find((item) => item.model);
                this.retryForm.model = defaultModel?.model || firstModel?.model || null;
            }
        },
        parseTaxIds(value) {
            if (Array.isArray(value)) {
                return value.map((item) => Number(item)).filter((item) => item > 0);
            }

            if (typeof value === 'string') {
                return value
                    .split(',')
                    .map((item) => Number(String(item).trim()))
                    .filter((item) => item > 0);
            }

            return [];
        },
        normalizeCustomFieldsValue(value) {
            if (Array.isArray(value)) {
                return value;
            }

            if (value && typeof value === 'object') {
                return value;
            }

            return {};
        },
        providerLabel(provider) {
            if (provider === 'openai') return 'OpenAI';
            if (provider === 'google') return 'Google';
            if (provider === 'anthropic') return 'Anthropic';
            return provider;
        },
        parseCustomFieldsJson() {
            try {
                const parsed = this.customFieldsJson ? JSON.parse(this.customFieldsJson) : {};
                if (!parsed || typeof parsed !== 'object') {
                    return { error: this.$t('message.invalid_json') };
                }

                return { value: parsed };
            } catch (e) {
                return { error: this.$t('message.invalid_json') };
            }
        },
        buildStartConfigOverrides() {
            const parsedCustomFields = this.parseCustomFieldsJson();
            if (parsedCustomFields.error) {
                return { error: parsedCustomFields.error };
            }

            const provider = this.normalizeScalarSelectValue(this.startForm.provider);
            const model = this.normalizeScalarSelectValue(this.startForm.model);

            return {
                value: {
                    max_items_per_chunk: this.startForm.max_items_per_chunk,
                    provider,
                    model,
                    tax_ids: this.startForm.tax_ids,
                    unit_id: this.startForm.unit_id,
                    barcode_id: this.startForm.barcode_id,
                    wholesale: this.startForm.wholesale,
                    wholesale_min: this.startForm.wholesale_min,
                    custom_fields: parsedCustomFields.value,
                },
            };
        },
        updateSnapshot() {
            if (!this.canStart) {
                return;
            }

            const payload = this.buildStartConfigOverrides();
            if (payload.error) {
                alertService.error(payload.error);
                return;
            }

            this.isUpdatingPendingSnapshot = true;
            this.$store.dispatch('aiImport/updateSnapshot', {
                id: this.$route.params.id,
                form: {
                    config_overrides: payload.value,
                },
            }).then(() => {
                alertService.success(this.$t('message.updated_settings'));
                this.$nextTick(() => {
                    this.syncStartFormWithDefaults();
                });
            }).catch((err) => {
                alertService.error(err.response?.data?.message || err.message);
            }).finally(() => {
                this.isUpdatingPendingSnapshot = false;
            });
        },
        toggleConfigForm() {
            this.isConfigFormExpanded = !this.isConfigFormExpanded;
        },
        updateRetryLlmConfig() {
            if (!this.canRetryImport) {
                return;
            }

            const payload = this.buildRetryConfigOverrides();
            if (payload.error) {
                alertService.error(payload.error);
                return;
            }

            this.isUpdatingRetryConfig = true;
            this.$store.dispatch('aiImport/updateSnapshot', {
                id: this.$route.params.id,
                form: {
                    config_overrides: payload.value,
                },
            }).then(() => {
                alertService.success(this.$t('message.updated_settings'));
                this.$nextTick(() => {
                    this.syncStartFormWithDefaults();
                });
            }).catch((err) => {
                alertService.error(err.response?.data?.message || err.message);
            }).finally(() => {
                this.isUpdatingRetryConfig = false;
            });
        },
        toggleRetryConfigForm() {
            this.isRetryConfigFormExpanded = !this.isRetryConfigFormExpanded;
        },
        buildRetryConfigOverrides() {
            const provider = this.normalizeScalarSelectValue(this.retryForm.provider);
            const model = this.normalizeScalarSelectValue(this.retryForm.model);

            if (!provider || !model) {
                return { error: this.$t('message.required_fields') };
            }

            return {
                value: {
                    provider,
                    model,
                },
            };
        },
        startImport() {
            if (!this.canStartFlow) {
                return;
            }

            const payload = this.canRetryImport
                ? this.buildRetryConfigOverrides()
                : this.buildStartConfigOverrides();
            if (payload.error) {
                alertService.error(payload.error);
                return;
            }

            this.isStarting = true;
            this.$store.dispatch('aiImport/start', {
                id: this.$route.params.id,
                form: {
                    config_overrides: payload.value,
                },
            }).then(() => {
                alertService.success(this.$t('message.ai_import_started'));
                this.$router.push({ name: 'admin.products.aiImport' });
            }).catch((err) => {
                alertService.error(err.response?.data?.message || err.message);
            }).finally(() => {
                this.isStarting = false;
            });
        },
        retryImport() {
            if (!this.canStartFlow) {
                return;
            }

            this.startImport();
        },
        updateItemField(item, field, value, options = {}) {
            if (!this.isEditable) {
                return;
            }

            const useModalLoading = !!options.useModalLoading;
            const skipRefresh = !!options.skipRefresh;
            if (useModalLoading) {
                this.beginModalLoading(field);
            }

            if (field === 'category') {
                const incomingCategoryIds = this.normalizeCategorySelection(value);
                const currentCategoryIds = this.normalizeCategorySelection(
                    this.processedField(item, 'category'),
                    item?.product?.category_ids || []
                );

                if (JSON.stringify(currentCategoryIds) === JSON.stringify(incomingCategoryIds)) {
                    if (useModalLoading) {
                        this.endModalLoading();
                    }
                    return;
                }

                if (!useModalLoading) {
                    this.updatingTableItemId = item.id;
                }
                this.$store.dispatch('aiImport/updateItem', {
                    id: item.id,
                    form: {
                        category: incomingCategoryIds.length ? incomingCategoryIds[0] : null,
                        category_ids: incomingCategoryIds,
                    }
                }).then(async () => {
                    if (useModalLoading) {
                        await this.refreshImportSilently();
                        this.syncModalItemFromStore(item.id);
                    } else if (!skipRefresh) {
                        await this.refreshImportSilently();
                    }
                }).catch((err) => {
                    alertService.error(err.response?.data?.message || err.message);
                    if (!useModalLoading && !skipRefresh) {
                        this.refreshImportSilently();
                    }
                }).finally(() => {
                    if (useModalLoading) {
                        this.endModalLoading();
                    } else {
                        this.updatingTableItemId = null;
                    }
                });

                return;
            }

            if (field === 'custom_fields') {
                const currentCustomFields = this.normalizeCustomFieldsForSave(this.processedField(item, 'custom_fields'));
                const incomingCustomFields = this.normalizeCustomFieldsForSave(value);

                if (JSON.stringify(currentCustomFields) === JSON.stringify(incomingCustomFields)) {
                    if (useModalLoading) {
                        this.endModalLoading();
                    }
                    return;
                }

                if (!useModalLoading) {
                    this.updatingTableItemId = item.id;
                }
                this.$store.dispatch('aiImport/updateItem', {
                    id: item.id,
                    form: { custom_fields: incomingCustomFields }
                }).then(async () => {
                    if (useModalLoading) {
                        await this.refreshImportSilently();
                        this.syncModalItemFromStore(item.id);
                    } else {
                        await this.refreshImportSilently();
                    }
                }).catch((err) => {
                    alertService.error(err.response?.data?.message || err.message);
                    if (!useModalLoading) {
                        this.refreshImportSilently();
                    }
                }).finally(() => {
                    if (useModalLoading) {
                        this.endModalLoading();
                    } else {
                        this.updatingTableItemId = null;
                    }
                });

                return;
            }

            const current = this.currentFieldValue(item, field);
            const incoming = value === null || value === undefined ? '' : String(value);

            const normalizedCurrent = this.normalizeFieldValue(field, current);
            const normalizedIncoming = this.normalizeFieldValue(field, incoming);

            if (normalizedCurrent === normalizedIncoming) {
                if (useModalLoading) {
                    this.endModalLoading();
                }
                return;
            }

            if (current === incoming) {
                if (useModalLoading) {
                    this.endModalLoading();
                }
                return;
            }

            if (!useModalLoading) {
                this.updatingTableItemId = item.id;
            }
            this.$store.dispatch('aiImport/updateItem', {
                id: item.id,
                form: { [field]: incoming }
            }).then(async () => {
                if (useModalLoading) {
                    await this.refreshImportSilently();
                    this.syncModalItemFromStore(item.id);
                } else if (!skipRefresh) {
                    await this.refreshImportSilently();
                }
            }).catch((err) => {
                alertService.error(err.response?.data?.message || err.message);
                if (!useModalLoading && !skipRefresh) {
                    this.refreshImportSilently();
                }
            }).finally(() => {
                if (useModalLoading) {
                    this.endModalLoading();
                } else {
                    this.updatingTableItemId = null;
                }
            });
        },
        simpleDateTime(value) {
            if (!value) {
                return '--';
            }
            return appService.convertOnlyDateTime(value);
        },
        formatPrice2(value) {
            const numeric = Number(value ?? 0);
            return Number.isFinite(numeric) ? numeric.toFixed(2) : '0.00';
        },
        formatTags(tags) {
            if (Array.isArray(tags)) {
                return tags.join(', ');
            }

            if (typeof tags === 'string') {
                return tags;
            }

            return '';
        },
        processedField(item, field) {
            const product = item?.product || {};
            if (product[field] !== undefined && product[field] !== null) {
                return product[field];
            }

            return item?.[field] ?? '';
        },
        currentFieldValue(item, field) {
            const value = this.processedField(item, field);
            if (field === 'tags') {
                return this.formatTags(value);
            }

            return value === null || value === undefined ? '' : String(value);
        },
        normalizeFieldValue(field, value) {
            if (field === 'tags') {
                return this.normalizeTagsString(value);
            }

            if (this.isModalPriceField(field)) {
                return this.normalizePriceTo2(value);
            }

            return String(value ?? '').trim();
        },
        isModalPriceField(field) {
            return ['buying_price', 'selling_price', 'wholesale_price'].includes(field);
        },
        normalizePriceTo2(value) {
            const parsed = Number(value);
            if (!Number.isFinite(parsed)) {
                return '';
            }

            return parsed.toFixed(2);
        },
        normalizeTagsString(value) {
            return String(value ?? '')
                .split(',')
                .map((item) => item.trim())
                .filter((item) => item !== '')
                .join(',');
        },
        selectedValuesFromEvent(event) {
            if (!event?.target?.selectedOptions) {
                return [];
            }

            return Array.from(event.target.selectedOptions)
                .map((option) => Number(option.value))
                .filter((value) => Number.isInteger(value) && value > 0);
        },
        normalizeCategorySelection(category, fallback = []) {
            const source = Array.isArray(category)
                ? category
                : (category === null || category === undefined || category === ''
                    ? fallback
                    : [category]);

            return source
                .map((value) => Number(value))
                .filter((value) => Number.isInteger(value) && value > 0)
                .filter((value, index, array) => array.indexOf(value) === index);
        },
        normalizeSingleSelectValue(value, options = []) {
            const numeric = Number(value);
            if (!Number.isInteger(numeric) || numeric <= 0) {
                return null;
            }

            const exists = options.some((option) => Number(option?.id) === numeric);
            return exists ? numeric : null;
        },
        modalSelectOptions(field) {
            if (field === 'category') return this.categoryOptions;
            if (field === 'product_brand_id') return this.brandOptions;
            if (field === 'unit_id') return this.unitOptions;
            return [];
        },
        safeModalSelectValue(field) {
            const options = this.modalSelectOptions(field);

            if (field === 'category') {
                const current = this.normalizeCategorySelection(this.productDetailModal.form?.[field] || []);
                return current.filter((value) => options.some((option) => Number(option?.id) === Number(value)));
            }

            return this.normalizeSingleSelectValue(this.productDetailModal.form?.[field], options);
        },
        normalizeModalSelectPayload(field, value) {
            const options = this.modalSelectOptions(field);

            if (field === 'category') {
                const values = Array.isArray(value) ? value : (value === null || value === undefined ? [] : [value]);
                const normalized = this.normalizeCategorySelection(values);
                return normalized.filter((item) => options.some((option) => Number(option?.id) === Number(item)));
            }

            return this.normalizeSingleSelectValue(value, options);
        },
        normalizeCustomFieldsForEditor(rows) {
            if (!Array.isArray(rows)) {
                if (rows && typeof rows === 'object') {
                    return Object.keys(rows).map((key) => ({
                        key: String(key),
                        label: String(key),
                        value: rows[key] === null || rows[key] === undefined ? '' : String(rows[key]),
                    }));
                }

                return [];
            }

            return rows.map((row) => ({
                key: String(row?.key || '').trim(),
                label: String(row?.label || row?.key || '').trim(),
                value: row?.value === null || row?.value === undefined ? '' : String(row.value),
            })).filter((row) => row.key !== '');
        },
        normalizeCustomFieldsForSave(rows) {
            if (!Array.isArray(rows)) {
                return [];
            }

            return rows.map((row) => ({
                key: String(row?.key || '').trim(),
                label: String(row?.label || row?.key || '').trim(),
                value: row?.value === null || row?.value === undefined ? '' : String(row.value),
            })).filter((row) => row.key !== '');
        },
        flattenCategoryTree(tree) {
            const flat = [];
            const walk = (nodes, prefix = '') => {
                (nodes || []).forEach((node) => {
                    const id = Number(node?.id || 0);
                    const name = String(node?.name || '').trim();
                    if (id > 0 && name !== '') {
                        flat.push({
                            id,
                            name: prefix ? `${prefix} / ${name}` : name,
                        });
                    }

                    if (Array.isArray(node?.children) && node.children.length) {
                        walk(node.children, prefix ? `${prefix} / ${name}` : name);
                    }
                });
            };

            walk(tree);

            return flat;
        },
        openProductDetailModal(item) {
            const fields = [
                'name',
                'sku',
                'category',
                'product_brand_id',
                'unit_id',
                'tags',
                'buying_price',
                'selling_price',
                'wholesale_price',
                'stock_quantity',
                'weight',
                'height',
                'width',
                'length',
                'description',
                'short_description',
            ];

            const form = {};
            fields.forEach((field) => {
                if (field === 'tags') {
                    form[field] = this.formatTags(this.getFieldValue(item, field));
                    return;
                }

                const value = this.getFieldValue(item, field);
                if (field === 'category') {
                    form[field] = this.normalizeCategorySelection(value, item?.product?.category_ids || []);
                } else {
                    form[field] = this.isModalPriceField(field)
                        ? this.normalizePriceTo2(value)
                        : (value === null || value === undefined ? '' : String(value));
                }
            });

            this.productDetailModal.item = item;
            this.productDetailModal.form = form;
            this.productDetailModal.form.custom_fields = this.normalizeCustomFieldsForEditor(this.getFieldValue(item, 'custom_fields'));
            this.productDetailModal.activeField = null;
            this.productDetailModal.visible = true;
        },
        closeProductDetailModal() {
            this.productDetailModal.visible = false;
            this.productDetailModal.item = null;
            this.productDetailModal.form = {};
            this.productDetailModal.activeField = null;
        },
        activateModalField(field) {
            if (!this.isEditable || this.isModalBusy) {
                return;
            }

            this.productDetailModal.activeField = field;
            this.$nextTick(() => {
                const refName = `modalField_${field}`;
                const inputRef = this.$refs[refName];
                const target = Array.isArray(inputRef) ? inputRef[0] : inputRef;
                if (target && typeof target.focus === 'function') {
                    target.focus();
                    if (typeof target.select === 'function') {
                        target.select();
                    }
                }
            });
        },
        modalDisplayValue(field) {
            const value = this.productDetailModal.form?.[field] ?? '';
            if (this.isModalPriceField(field)) {
                return this.normalizePriceTo2(value);
            }

            return value;
        },
        onModalFieldChange(field, value) {
            this.productDetailModal.form[field] = value;
        },
        onModalSelectChange(field, value) {
            const normalized = this.normalizeModalSelectPayload(field, value);
            this.productDetailModal.form[field] = normalized;
        },
        deactivateModalField(field, value) {
            const normalizedValue = this.isModalPriceField(field)
                ? this.normalizePriceTo2(value)
                : value;
            this.productDetailModal.form[field] = normalizedValue;
            this.productDetailModal.activeField = null;
        },
        onCustomFieldBlur(index, value) {
            const rows = [...(this.productDetailModal.form.custom_fields || [])];
            if (!rows[index]) {
                return;
            }
            rows[index] = { ...rows[index], value };
            this.productDetailModal.form.custom_fields = rows;
            this.productDetailModal.activeField = null;
        },
        async updateModalItem() {
            const item = this.productDetailModal.item;
            if (!item || !this.isEditable || this.productDetailModal.isUpdating || this.productDetailModal.isConfirming) {
                return;
            }

            this.productDetailModal.isUpdating = true;
            try {
                const form = this.productDetailModal.form;
                const categoryIds = this.normalizeCategorySelection(form.category || []);
                const customFields = this.normalizeCustomFieldsForSave(form.custom_fields || []);

                await this.$store.dispatch('aiImport/updateItem', {
                    id: item.id,
                    form: {
                        name: form.name,
                        sku: form.sku,
                        category: categoryIds.length ? categoryIds[0] : null,
                        category_ids: categoryIds,
                        product_brand_id: form.product_brand_id || null,
                        unit_id: form.unit_id || null,
                        tags: form.tags,
                        buying_price: form.buying_price,
                        selling_price: form.selling_price,
                        wholesale_price: form.wholesale_price,
                        stock_quantity: form.stock_quantity,
                        weight: form.weight,
                        height: form.height,
                        width: form.width,
                        length: form.length,
                        short_description: form.short_description,
                        description: form.description,
                        custom_fields: customFields,
                    },
                });

                await this.refreshImportSilently();
                this.syncModalItemFromStore(item.id);
                alertService.success(this.$t('message.update'));
            } catch (err) {
                alertService.error(err.response?.data?.message || err.message);
            } finally {
                this.productDetailModal.isUpdating = false;
            }
        },
        saveModalField(field, directValue = null) {
            if (!this.productDetailModal.item || this.isModalBusy) {
                return;
            }

            const value = directValue === null
                ? (this.productDetailModal.form[field] ?? '')
                : directValue;

            const normalizedValue = this.isModalPriceField(field)
                ? this.normalizePriceTo2(value)
                : value;

            this.productDetailModal.form[field] = normalizedValue;
            this.productDetailModal.activeField = null;

            this.updateItemField(this.productDetailModal.item, field, normalizedValue, { useModalLoading: true });
        },
        saveModalCustomField(index, value) {
            if (this.isModalBusy) {
                return;
            }

            const rows = [...(this.productDetailModal.form.custom_fields || [])];
            if (!rows[index]) {
                return;
            }

            rows[index] = {
                ...rows[index],
                value,
            };

            this.productDetailModal.form.custom_fields = rows;
            this.productDetailModal.activeField = null;

            this.updateItemField(this.productDetailModal.item, 'custom_fields', this.normalizeCustomFieldsForSave(rows), { useModalLoading: true });
        },
        beginModalLoading(field = null) {
            this.productDetailModal.isLoading = true;
            this.productDetailModal.lockField = field;
        },
        endModalLoading() {
            this.productDetailModal.isLoading = false;
            this.productDetailModal.lockField = null;
        },
        guardFocusDuringModalUpdate(event) {
            if (!this.isModalBusy) {
                return;
            }

            if (event?.target && typeof event.target.blur === 'function') {
                event.target.blur();
            }
        },
        async refreshImportSilently() {
            await this.$store.dispatch('aiImport/show', this.$route.params.id);
        },
        syncModalItemFromStore(itemId) {
            const refreshedItem = this.items.find((item) => item.id === itemId);
            if (!refreshedItem) {
                return;
            }

            this.productDetailModal.item = refreshedItem;
            this.productDetailModal.form.category = this.normalizeCategorySelection(
                this.processedField(refreshedItem, 'category'),
                refreshedItem?.product?.category_ids || []
            );
        },
        openNoteModal(message, name) {
            this.noteModal.message = message || '--';
            this.noteModal.name = name || '';
            this.noteModal.visible = true;
        },
        closeNoteModal() {
            this.noteModal.visible = false;
            this.noteModal.message = '';
            this.noteModal.name = '';
        },
        openPurgeModal() {
            if (!this.canPurgeImport || this.isPurgingImport) {
                return;
            }

            this.purgeModal.visible = true;
        },
        closePurgeModal() {
            if (this.isPurgingImport) {
                return;
            }

            this.purgeModal.visible = false;
        },
        async confirmPurgeModal() {
            if (!this.aiImport?.id || this.isPurgingImport) {
                return;
            }

            this.isPurgingImport = true;
            try {
                await this.$store.dispatch('aiImport/purgeFailed', this.aiImport.id);
                alertService.success('Importacion eliminada y limpieza aplicada.');
                this.closePurgeModal();
                this.$router.push({ name: 'admin.products.aiImport' });
            } catch (err) {
                alertService.error(err.response?.data?.message || err.message);
            } finally {
                this.isPurgingImport = false;
            }
        },
        statusLabel(status) {
            const statuses = {
                5: this.$t('label.pending'),
                10: this.$t('label.in_progress'),
                15: this.$t('label.processed'),
                20: this.$t('label.incomplete'),
                25: this.$t('label.finished'),
                30: this.$t('label.failed'),
            };
            return statuses[status] || status;
        },
        itemState(item) {
            if (item.status === 30) {
                return this.$t('label.failed');
            }
            if (item.status === 25) {
                return this.$t('label.finished');
            }
            if (item.status === 20) {
                return this.$t('label.incomplete');
            }
            if (item.is_incomplete) {
                return this.$t('label.incomplete');
            }
            if (item.is_duplicate_sku) {
                return this.$t('label.duplicate');
            }
            if (item.status === 15) {
                return this.$t('label.processed');
            }
            if (item.status === 10) {
                return this.$t('label.in_progress');
            }
            if (item.status === 5) {
                return this.$t('label.pending');
            }
            return this.$t('label.complete');
        },
        itemBadge(item) {
            if (item.status === 30) {
                return 'db-badge pt-1.5 pb-1 border border-red-500 bg-red-100 text-red-500';
            }
            if (item.status === 25) {
                return 'db-badge pt-1.5 pb-1 border border-gray-600 text-gray-600';
            }
            if (item.status === 20) {
                return 'db-badge pt-1.5 pb-1 border border-warning bg-orange-100 text-orange-500';
            }
            if (item.is_incomplete) {
                return 'db-badge pt-1.5 pb-1 border border-warning bg-orange-100 text-orange-500';
            }
            if (item.is_duplicate_sku) {
                return 'db-badge pt-1.5 pb-1 border border-amber-500 bg-amber-100 text-amber-500';
            }
            if (item.status === 15) {
                return 'db-badge pt-1.5 pb-1 border border-gray-600 text-gray-600';
            }
            if (item.status === 10) {
                return 'db-badge pt-1.5 pb-1 border border-gray-600 text-gray-600';
            }
            if (item.status === 5) {
                return 'db-badge pt-1.5 pb-1 border border-gray-600 bg-gray-100 text-gray-600';
            }
            return 'db-badge pt-1.5 pb-1 border border-gray-600 text-gray-600';
        },
        canConfirmItem(item) {
            if (!item.product_id) {
                return false;
            }

            const isItemProcessedAndComplete = Number(item.status) === 15 && !item.is_incomplete && !item.is_duplicate_sku;
            return isItemProcessedAndComplete && [15, 20, 25].includes(Number(this.aiImport?.status));
        },
        confirmItem(item, options = {}) {
            const fromModal = !!options.fromModal;
            if (fromModal) {
                if (this.productDetailModal.isUpdating || this.productDetailModal.isConfirming) {
                    return;
                }
                this.productDetailModal.isConfirming = true;
            } else {
                if (this.confirmingItemId) {
                    return;
                }
                this.confirmingItemId = item.id;
            }

            this.$store.dispatch('aiImport/confirmItem', item.id).then(async () => {
                alertService.success(this.$t('message.ai_import_item_confirmed'));
                if (fromModal) {
                    await this.refreshImportSilently();
                    this.syncModalItemFromStore(item.id);
                } else {
                    await this.refreshImportSilently();
                }
            }).catch((err) => {
                alertService.error(err.response?.data?.message || err.message);
            }).finally(() => {
                if (fromModal) {
                    this.productDetailModal.isConfirming = false;
                } else {
                    this.confirmingItemId = null;
                }
            });
        },
        async deleteItem(item) {
            if (this.deletingItemId) {
                return;
            }

            appService.destroyConfirmation().then(async () => {
                this.deletingItemId = item.id;
                try {
                    await this.$store.dispatch('aiImport/deleteItem', item.id);
                    alertService.success(this.$t('message.ai_import_item_deleted') || 'Elemento y producto eliminados.');
                    await this.refreshImportSilently();
                } catch (err) {
                    alertService.error(err.response?.data?.message || err.message);
                } finally {
                    this.deletingItemId = null;
                }
            }).catch(() => {});
        },
        getFieldValue(item, field) {
            return this.pendingChanges[item.id]?.[field] ?? this.processedField(item, field);
        },
        setFieldValue(item, field, value) {
            this.pendingChanges = {
                ...this.pendingChanges,
                [item.id]: {
                    ...this.pendingChanges[item.id],
                    [field]: value
                }
            };
        },
        hasPendingChanges(item) {
            const changes = this.pendingChanges[item.id] || {};
            return Object.keys(changes).some(field => {
                const pending = changes[field];
                const current = this.processedField(item, field);
                if (field === 'tags') {
                    return this.normalizeTagsString(pending) !== this.normalizeTagsString(this.formatTags(current));
                }
                if (field === 'category') {
                    const pendingNorm = this.normalizeCategorySelection(pending);
                    const currentNorm = this.normalizeCategorySelection(current, item?.product?.category_ids || []);
                    return JSON.stringify(pendingNorm.sort()) !== JSON.stringify(currentNorm.sort());
                }
                const pendingNorm = String(pending || '').trim();
                const currentNorm = String(current || '').trim();
                return pendingNorm !== currentNorm;
            });
        },
        checkAutoUpdate(item) {
            const fields = ['name', 'category', 'tags', 'weight', 'height', 'width', 'length'];
            const hasAllData = fields.every(field => {
                const value = this.getFieldValue(item, field);
                return value !== null && value !== undefined && String(value).trim() !== '';
            });
            if (hasAllData && this.hasPendingChanges(item)) {
                this.updateRow(item);
            }
        },
        onFieldBlur(item, field, value) {
            this.setFieldValue(item, field, value);
            this.checkAutoUpdate(item);
        },
        onFieldChange(item, field, value) {
            this.setFieldValue(item, field, value);
            this.checkAutoUpdate(item);
        },
        async updateRow(item) {
            const changes = this.pendingChanges[item.id];
            if (!changes) return;
            this.updatingTableItemId = item.id;
            const promises = Object.keys(changes).map(field => this.updateItemField(item, field, changes[field], { skipRefresh: true }));
            try {
                await Promise.all(promises);
                await this.refreshImportSilently();
                alertService.success(this.$t('message.update'));
                const pendingChanges = { ...this.pendingChanges };
                delete pendingChanges[item.id];
                this.pendingChanges = pendingChanges;
            } catch (err) {
                alertService.error(err.response?.data?.message || err.message);
            } finally {
                this.updatingTableItemId = null;
            }
        },
        async confirmAllItems() {
            if (this.isConfirmingAll) {
                return;
            }

            const eligibleItems = this.items.filter((item) => this.canConfirmItem(item));
            if (eligibleItems.length === 0) {
                return;
            }

            this.isConfirmingAll = true;
            try {
                for (const item of eligibleItems) {
                    await this.$store.dispatch('aiImport/confirmItem', item.id);
                }
                alertService.success(this.$t('message.ai_import_item_confirmed'));
            } catch (err) {
                alertService.error(err.response?.data?.message || err.message);
            } finally {
                await this.refreshImportSilently();
                this.isConfirmingAll = false;
            }
        }
    }
};
</script>

<style scoped>
.product-detail-modal.is-busy .f-b-custom-select,
.product-detail-modal.is-busy .f-b-custom-select * {
    z-index: 1 !important;
}

.modal-inline-loader {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
}

.modal-inline-loader.velmld-parent {
    width: 100%;
    height: 100%;
}

.modal-inline-loader.velmld-parent .velmld-overlay {
    position: absolute !important;
    inset: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    top: 0 !important;
    left: 0 !important;
}

.config-chevron {
    transition: transform 0.2s ease;
}

.config-chevron.is-open {
    transform: rotate(180deg);
}
</style>
